import React, { useState, useEffect, useCallback, useId } from 'react';
import { MdTune, MdClose } from 'react-icons/md';
import { fetchAll } from 'config/fetchAll';
import { useTranslation } from 'react-i18next';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { useModalDialog } from 'reusable/useModalDialog';

interface FilterProps {
  onTaskSelect: (taskId: string | null, taskName: string) => void;
  onClientSelect: (clientIds: string[], clientNames: string[]) => void;
}

const Filter: React.FC<FilterProps> = ({ onTaskSelect, onClientSelect }) => {
  const { t } = useTranslation('client');
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTask, setSelectedTask] = useState<{
    id: string | null;
    name: string;
  }>({ id: null, name: 'All Tasks' });
  const [selectedClients, setSelectedClients] = useState<
    { id: string; name: string }[]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');
  // 空は未選択。表示は翻訳した「フィルター」にする。
  const [filterDisplay, setFilterDisplay] = useState('');

  const [tasks, setTasks] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);

  const [tempTask, setTempTask] = useState(selectedTask);
  const [tempClients, setTempClients] = useState<
    { id: string; name: string }[]
  >([]);

  const beginRequest = useLatestRequest();
  const [fetchErrors, setFetchErrors] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchOptions = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true); setFetchErrors([]);
    const results = await Promise.allSettled([
      fetchAll<any>('/v1/tasks/'), fetchAll<any>('/v1/client_deliveries/'),
    ]);
    if (!isCurrent()) return;
    if (results[0].status === 'fulfilled') setTasks(results[0].value);
    if (results[1].status === 'fulfilled') setClients(results[1].value);
    setFetchErrors(results.map(result => result.status === 'rejected' ? result.reason : null));
    setLoading(false);
  }, [beginRequest]);
  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  useEffect(() => {
    const formatList = (list: { name: string }[], maxItems: number): string => {
      if (list.length <= maxItems) {
        return list.map((item) => item.name).join(', ');
      }
      return `${list
        .slice(0, maxItems)
        .map((item) => item.name)
        .join(', ')}...`;
    };

    if (selectedTask.id === null && selectedClients.length === 0) {
      setFilterDisplay('');
    } else {
      const taskDisplay =
        selectedTask.id === null ? '' : selectedTask.name;
      const clientsDisplay =
        selectedClients.length === 0 ? '' : formatList(selectedClients, 5);
      setFilterDisplay(
        [taskDisplay, clientsDisplay].filter(Boolean).join(', ')
      );
    }
  }, [selectedTask, selectedClients]);

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  // ADM-12。作業・配送先の名前は一意ではない。選択は ID で持ち、名前から ID を引かない
  // （同じ名前の2件目を選んでも、1件目の ID を送っていた）。
  const handleTaskChange = (task: { id: string | null; name: string }) => {
    setTempTask(task);
  };

  const handleClientChange = (client: { id: string; name: string }) => {
    setTempClients((prevClients) =>
      prevClients.some((c) => String(c.id) === String(client.id))
        ? prevClients.filter((c) => String(c.id) !== String(client.id))
        : [...prevClients, { id: client.id, name: client.name }]
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const deselectAllClients = () => {
    setTempClients([]);
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const applyFilters = () => {
    setSelectedTask(tempTask);
    setSelectedClients(tempClients);
    onTaskSelect(tempTask.id, tempTask.name);
    onClientSelect(
      tempClients.map((client) => client.id),
      tempClients.map((client) => client.name)
    );
    togglePopup();
  };

  // 適用済みの絞り込みを外す（絞り込みの表示の右の × ボタン）。親へ「絞り込みなし」を知らせる。
  const clearFilters = () => {
    setSelectedTask({ id: null, name: 'All Tasks' });
    setSelectedClients([]);
    setTempTask({ id: null, name: 'All Tasks' });
    setTempClients([]);
    setSearchQuery('');
    setShowPopup(false);
    onTaskSelect(null, 'All Tasks');
    onClientSelect([], []);
  };

  // ADM-16（2026-10-11 PM 判断）。「閉じる」・Escape は選びかけだけを捨て、適用済みの絞り込みは残す。
  // 以前は clearFilters と同じで、ダイアログを開いて閉じるだけで適用中の絞り込みが外れていた。
  const discardChanges = () => {
    setTempTask(selectedTask);
    setTempClients(selectedClients);
    setSearchQuery('');
    setShowPopup(false);
  };
  // ADM-16。絞り込みのダイアログ。Escape は「閉じる」と同じ（選びかけを捨てる）。
  // RV-125。ダイアログの名前の id は文書の中で一意にする。
  const titleId = useId();
  const dialogRef = useModalDialog(showPopup, discardChanges);

  return (
    <div className="relative flex p-2 justify-center items-center border-r px-4 mr-3">
      <div className="flex flex-row cursor-pointer items-center">
        <button type="button"
          onClick={togglePopup}
          className={`flex items-center px-4 py-2 rounded-l-md ${
            filterDisplay === ''
              ? 'text-gray-400'
              : 'text-primary/300 bg-primary/50'
          }`}
        >
          {filterDisplay || t('filter.title')}
        </button>
        {filterDisplay === '' ? (
          // 左の「フィルター」と同じ操作。読み上げとキーボードでは左のボタンを使う。
          <button type="button" aria-hidden="true" tabIndex={-1} onClick={togglePopup}>
            <MdTune className="text-gray-400" />
          </button>
        ) : (
          <div className="py-3 px-1 bg-primary/50 rounded-r-md text-primary/300">
            <button
              type="button"
              aria-label={t('ui.clear-filter', { ns: 'common' })}
              onClick={clearFilters}
            >
              <MdClose aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 text-black">
          <div
            className="bg-white w-1/2 max-h-4/5 shadow-lg border rounded z-10 overflow-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={dialogRef}
            tabIndex={-1}
          >
            <h6 id={titleId} className="text-xl font-semibold mb-4 px-8 py-4 text-subtitle-1">
              {t('filter.title')}
            </h6>
            {loading && <p role="status">{t('ui.loading', { ns: 'common' })}</p>}
            <div className="border-t overflow-auto max-h-1/2">
              <h6 className="text-lg font-medium mb-2 px-8 py-2 text-gray-400 text-subtitle-2">
                {t('filter.by_task')}
              </h6>
              {fetchErrors[0] ? <FetchFailed error={fetchErrors[0]} onRetry={fetchOptions} /> : null}
              <div className="px-8 py-2">
                <div className="grid grid-cols-4 gap-2">
                  <label className="block mb-2">
                    <input
                      className="mr-2"
                      type="radio"
                      name="task"
                      value=""
                      checked={tempTask.id === null}
                      onChange={() => handleTaskChange({ id: null, name: 'All Tasks' })}
                    />
                    {t('filter.all_task')}
                  </label>
                  {tasks.map((task) => (
                    <label key={task.id} className="block mb-2">
                      <input
                        className="mr-2"
                        type="radio"
                        name="task"
                        value={String(task.id)}
                        checked={tempTask.id !== null && String(tempTask.id) === String(task.id)}
                        onChange={() => handleTaskChange({ id: task.id, name: task.name })}
                      />
                      {task.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-b overflow-auto max-h-1/2">
              <div className="flex flex-row justify-between items-center">
                <h6 className="text-4xl font-medium px-8 py-4">
                  {t('filter.by_client')}
                </h6>
                {fetchErrors[1] ? <FetchFailed error={fetchErrors[1]} onRetry={fetchOptions} /> : null}
                <button type="button"
                  onClick={deselectAllClients}
                  className="px-8 underline text-primary/200 rounded-md"
                >
                  {t('filter.deselect_all')}
                </button>
              </div>
              <div className="px-8 py-2">
                <input
                  type="text"
                  placeholder={t('filter.client_placeholder')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="border w-full rounded-md py-2 mb-4 text-left px-2"
                />
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <label key={client.id} className="block mb-2">
                      <input
                        className="mr-2"
                        type="checkbox"
                        value={String(client.id)}
                        checked={tempClients.some(
                          (c) => String(c.id) === String(client.id)
                        )}
                        onChange={() => handleClientChange({ id: client.id, name: client.name })}
                      />
                      {client.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 px-8 py-4">
              <button type="button"
                onClick={discardChanges}
                className="flex-1 px-4 py-2 bg-gray-300 rounded-md"
              >
                {t('action.close')}
              </button>
              <button type="button"
                onClick={applyFilters}
                disabled={loading || fetchErrors.some(Boolean)}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md"
              >
                {t('filter.title')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filter;
